<script setup lang="ts">
/** The single live region for the whole app; mounted once from `App.vue`. */
import { storeToRefs } from 'pinia'
import { useUiStore, type ToastVariant } from '@/stores/ui'
import Icon, { type IconName } from './Icon.vue'
import IconButton from './IconButton.vue'

const ui = useUiStore()
const { toasts } = storeToRefs(ui)

const ICONS: Record<ToastVariant, IconName> = {
  info: 'info',
  success: 'check-circle',
  warning: 'alert-triangle',
  danger: 'alert-circle',
}

function runAction(toast: { id: string; action?: { run: () => void } }): void {
  toast.action?.run()
  ui.dismissToast(toast.id)
}

const TONES: Record<ToastVariant, string> = {
  info: 'text-info',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
}
</script>

<template>
  <div
    class="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-4 sm:items-end"
    aria-live="polite"
    aria-atomic="false"
    data-print="hide"
  >
    <TransitionGroup
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="translate-y-2 opacity-0"
      leave-active-class="transition duration-100 ease-in absolute"
      leave-to-class="translate-y-1 opacity-0"
      move-class="transition duration-150"
    >
      <div
        v-for="toast in toasts"
        :key="toast.id"
        :role="toast.variant === 'danger' ? 'alert' : 'status'"
        class="pointer-events-auto flex w-full max-w-sm items-start gap-2.5 rounded-lg border border-line bg-raised px-3 py-2.5 shadow-pop"
      >
        <Icon
          :name="ICONS[toast.variant]"
          :size="16"
          :class="['mt-0.5', TONES[toast.variant]]"
        />
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-ink">
            {{ toast.title }}
          </p>
          <p
            v-if="toast.description"
            class="mt-0.5 text-xs text-muted"
          >
            {{ toast.description }}
          </p>
          <button
            v-if="toast.action"
            type="button"
            class="mt-1.5 rounded-xs text-xs font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            @click="runAction(toast)"
          >
            {{ toast.action.label }}
          </button>
        </div>
        <IconButton
          label="Meldung schließen"
          icon="x"
          size="xs"
          class="-mt-0.5 -mr-1"
          @click="ui.dismissToast(toast.id)"
        />
      </div>
    </TransitionGroup>
  </div>
</template>
